import React, { useState, useEffect } from "react";
import "./AddCourseTable.css";
import axios from "axios";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Box from "@mui/material/Box";
import { styled, alpha } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Modal from "@mui/material/Modal";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "50px",
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "27ch",
      "&:focus": {
        width: "35ch",
      },
    },
  },
}));

const AddCourseTable = ({ employeeList, setEmployeeList, forEmployees }) => {
  const [loading, setLoading] = useState(false);
  const [radioValue, setRadioValue] = useState("All");
  const [refresh, setRefresh] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [departments, setDepartments] = useState([]);
  
  const [viewData, setViewData] = useState({
    id: "",
    Email: "",
    Name: "",
    Occupation: "",
    Organization: "",
    Department: "",
    Status: "",
    dateCreated: "",
  });

  const handleRadioChange = (event) => {
    setRadioValue(event.target.value);
    setSelectedDepartment("All"); // Reset department filter when changing user type
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const handleClose = () => setOpen(false);
  
  const hasWindow = typeof window !== "undefined";
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  function getWindowDimensions() {
    const width = hasWindow ? window.innerWidth : null;
    return {
      width,
    };
  }

  // Extract unique departments from user data
  const extractDepartments = (usersData) => {
    const deptSet = new Set();
    usersData.forEach(user => {
      if (user.department) {
        deptSet.add(user.department);
      }
    });
    return ["All", ...Array.from(deptSet).sort()];
  };

  const getAllUsers = () => {
    setLoading(true);
    axios
      .post(process.env.REACT_APP_BACKEND_URL + "/api/users/getAllUsers", {
        token: localStorage.getItem("token"),
      })
      .then((res) => {
        let temp = res.data.data;
        
        // Extract departments from all users
        setDepartments(extractDepartments(temp));
        
        if (radioValue === "All") {
          setUsers(temp);
        } else if (radioValue === "Internal") {
          const found = temp.filter((obj) => {
            return obj.isEmployee.isTrue === true && obj.status === "Active";
          });
          setUsers(found);
        } else if (radioValue === "External") {
          const found = temp.filter((obj) => {
            return obj.isEmployee.isTrue === false && obj.status === "Active";
          });
          setUsers(found);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllUsers();
  }, [refresh]);

  useEffect(() => {
    if (users.length > 0) {
      setDepartments(extractDepartments(users));
    }
  }, [users]);

  const viewUser = (userData) => {
    setOpen(true);
    setViewData({
      id: userData.row.id,
      Status: userData.row.Status,
      Email: userData.row.Email,
      Name: userData.row.Name,
      Occupation: userData.row.Occupation,
      Organization: userData.row.Organization,
      Department: userData.row.department,
      Status: userData.row.Status,
      dateCreated: userData.row.dateCreated,
    });
  };

  // Define columns based on screen width
  let columns = [];
  if (windowDimensions.width >= 750) {
    columns = [
      {
        field: "Name",
        headerName: "Name",
        minWidth: 140,
      },
      {
        field: "Email",
        headerName: "Email",
        flex: 1,
      },
      {
        field: "Organization",
        headerName: "Organization",
        flex: 1,
      },
      {
        field: "department",
        headerName: "Department",
        flex: 1,
      },
      {
        field: "Occupation",
        headerName: "Occupation",
        flex: 1,
      },
      {
        field: "users",
        headerName: "Employee?",
        minWidth: 5,
        renderCell: (users) =>
          users.row.users === true ? (
            <div className="users__icon__green">
              <DoneIcon sx={{ color: "white" }} />
            </div>
          ) : (
            <div className="users__icon__red">
              <CloseIcon sx={{ color: "white" }} />
            </div>
          ),
      },
      {
        field: "Status",
        headerName: "Status",
        flex: 1,
      },
      {
        field: "Action",
        headerName: "Action",
        minWidth: 100,
        renderCell: (user) => (
          <>
            <Button
              onClick={() => viewUser(user)}
              variant="contained"
              size="small"
              sx={{ m: 1 }}
            >
              View
            </Button>
          </>
        ),
        sortable: false,
        filterable: false,
      },
    ];
  } else {
    columns = [
      {
        field: "Name",
        headerName: "Name",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Email",
        headerName: "Email",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Organization",
        headerName: "Organization",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "department",
        headerName: "Department",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "Occupation",
        headerName: "Occupation",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "users",
        headerName: "Employee?",
        flex: 1,
        minWidth: 90,
        renderCell: (users) =>
          users.row.users === true ? (
            <div className="users__icon__green">
              <DoneIcon sx={{ color: "white" }} />
            </div>
          ) : (
            <div className="users__icon__red">
              <CloseIcon sx={{ color: "white" }} />
            </div>
          ),
      },
      {
        field: "Status",
        headerName: "Status",
        flex: 1,
        minWidth: 90,
      },
      {
        field: "Action",
        headerName: "Action",
        minWidth: 180,
        renderCell: (user) => (
          <>
            <Button
              onClick={() => viewUser(user)}
              variant="contained"
              size="small"
              sx={{ m: 1 }}
            >
              View
            </Button>
          </>
        ),
        sortable: false,
        filterable: false,
      },
    ];
  }

  // Filter and map rows with department
  let rows = [];
  rows = users
    ?.filter((obj) => {
      // Check search filter
      const searchMatch = searchUser === "" || 
                         obj.name.toLowerCase().includes(searchUser?.toLowerCase());
      
      // Check department filter
      const deptMatch = selectedDepartment === "All" || 
                       obj.department === selectedDepartment;
      
      return searchMatch && deptMatch;
    })
    .map((cur, key) => {
      return {
        id: cur._id,
        Location: cur.jobCity,
        Name: cur.name,
        Email: cur.email,
        Organization: cur.organization,
        department: cur.department || "Not Employed",
        Occupation: cur.occupation,
        users: cur.isEmployee.isTrue,
        Status: cur.status,
        employeeID: cur.isEmployee.employeeID,
        dateCreated: cur.dateCreated,
      };
    });

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {forEmployees ? (
        <div className="UsersTable">
          <h3 style={{ textAlign: "center" }}>Users</h3>
          <Box sx={{ flexGrow: 1 }}>
            <AppBar
              position="static"
              sx={{
                bgcolor: "#41424C",
                borderRadius: "60px",
                margin: "10px auto",
                width: "85%",
                color: "white",
                padding: 0,
              }}
            >
              <Toolbar
                disableGutters
                sx={{ justifyContent: "space-between", padding: "0 4px" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* Search Bar */}
                  <Search>
                    <SearchIconWrapper>
                      <SearchIcon />
                    </SearchIconWrapper>
                    <StyledInputBase
                      placeholder="Search by name…"
                      inputProps={{ "aria-label": "search" }}
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                    />
                  </Search>

                  {/* Department Filter - Next to search bar */}
                  <Box sx={{ minWidth: 180 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel 
                        sx={{ 
                          color: "white",
                          "&.Mui-focused": {
                            color: "white"
                          }
                        }}
                      >
                        Department
                      </InputLabel>
                      <Select
                        value={selectedDepartment}
                        label="Department"
                        onChange={handleDepartmentChange}
                        sx={{
                          color: "white",
                          backgroundColor: alpha("#fff", 0.15),
                          borderRadius: "50px",
                          '&:hover': {
                            backgroundColor: alpha("#fff", 0.25),
                          },
                          '.MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.23)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.4)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.6)',
                          },
                          '.MuiSvgIcon-root': {
                            color: 'white',
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                            }
                          }
                        }}
                      >
                        {departments.map((dept) => (
                          <MenuItem key={dept} value={dept}>
                            {dept}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Radio buttons for user type */}
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={radioValue}
                  onChange={handleRadioChange}
                  onClick={() => setRefresh(!refresh)}
                  sx={{ ml: 2 }}
                >
                  <FormControlLabel
                    label="All"
                    value="All"
                    control={
                      <Radio
                        sx={{
                          "&, &.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    }
                  />
                  <FormControlLabel
                    label="Employees"
                    value="Internal"
                    control={
                      <Radio
                        sx={{
                          "&, &.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    }
                  />
                  <FormControlLabel
                    label="External"
                    value="External"
                    control={
                      <Radio
                        sx={{
                          "&, &.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    }
                  />
                </RadioGroup>
              </Toolbar>
            </AppBar>
          </Box>
          
          {users ? (
            <>
              <div style={{ margin: "10px auto", height: "70vh", width: "85%" }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={rowsPerPage}
                  onPageSizeChange={(newSize) => setRowsPerPage(newSize)}
                  rowsPerPageOptions={[10, 25, 50]}
                  pagination
                  disableSelectionOnClick
                  keepNonExistentRowsSelected={true}
                  checkboxSelection
                  selectionModel={employeeList?.map((cur) => {
                    return cur._id;
                  })}
                  onSelectionModelChange={(ids) => {
                    const selectedIDs = new Set(ids);
                    const selectedRowData = users.filter((row) =>
                      selectedIDs.has(row._id)
                    );
                    let temp = selectedRowData?.map((cur) => {
                      return { 
                        _id: cur._id, 
                        employeeID: cur.isEmployee.employeeID,
                        department: cur.department
                      };
                    });
                    setEmployeeList(temp);
                  }}
                />
              </div>
              
              <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
              >
                <Box sx={style} className="viewUser__modal">
                  <div className="viewUser__modal__heading">User Details</div>
                  <div className="user__details__box">
                    <div className="user__detail">
                      <div className="user__detail__title">Name: </div>
                      <div className="user__detail__text">{viewData.Name}</div>
                    </div>
                    <div className="user__detail">
                      <div className="user__detail__title">Email:</div>
                      <div className="user__detail__text">{viewData.Email}</div>
                    </div>
                    <div className="user__detail">
                      <div className="user__detail__title">Occupation:</div>
                      <div className="user__detail__text">
                        {viewData.Occupation}
                      </div>
                    </div>
                    <div className="user__detail">
                      <div className="user__detail__title">Department:</div>
                      <div className="user__detail__text">
                        {viewData.Department}
                      </div>
                    </div>
                    <div className="user__detail">
                      <div className="user__detail__title">Organization: </div>
                      <div className="user__detail__text">
                        {viewData.Organization}
                      </div>
                    </div>
                    <div className="user__detail">
                      <div className="user__detail__title">Status: </div>
                      <div className="user__detail__text">
                        {viewData.Status}
                      </div>
                    </div>
                    <div className="user__detail">
                      <div className="user__detail__title">Date Created: </div>
                      <div className="user__detail__text">
                        {viewData.dateCreated}
                      </div>
                    </div>
                  </div>
                </Box>
              </Modal>
              
              <Backdrop
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={loading}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
            </>
          ) : (
            <div>
              <Backdrop
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={true}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
};

export default AddCourseTable;